import { action, makeAutoObservable } from 'mobx'
import icons from '../../assets/icons'
import { Blocker } from 'react-router'
import { $RootStore } from '../RootStore'
import { DropdownAlertType } from '../DropdownMessageHandler'
import { Operation } from '@apollo/client'
import { iconColors } from '~/theme/default'
import { defaultNumbers } from '../../../shared-solo/viewModels/BaseHomeScreenVM'
import { $CalendarView, $CalendarZoomLevel, calendarZoomLevels } from '~/routes/calendar/agendaViewConfig'
import { CalendarOptions } from 'shared-solo/lib/calendarOptions'
import { PopoverProps } from '@mui/material'
import { $BottomMessageData } from '../BottomMessageHandler'
import { StatsKeysWithSource } from 'shared-solo/stores/StatisticsStore/StatisticsStore'

export type $BottomMessageDataImage = $BottomMessageData['bannerImage']

// Should be shared
export interface $ActionMenuData {
  title?: string
  actions?: Array<$ActionMenuAction>
  desktopAnchorEl?: string
  anchorOrigin?: PopoverProps['anchorOrigin']
  transformOrigin?: PopoverProps['transformOrigin']
  zIndex?: number
}

export interface $UIMessage {
  title?: string
  body?: string
  buttons?: Array<$UIMessageButton>
  important?: boolean
  visible?: boolean
  icon?: keyof typeof icons
  scrollable?: boolean
  isMarkdown?: boolean
}

export interface $UIMessageButton {
  title: string
  type?: 'primary' | 'error' | 'secondary'
  onPress?: () => void
  preventModalClose?: boolean
}

// Should be shared
export interface $ActionMenuAction {
  title: string
  action: () => void
  disabled?: boolean
  icon?: keyof typeof icons
  iconSize?: number
  color?: 'primary' | 'danger'
  iconColor?: 'primary' | 'red'
}

const defaultCalendarHourHeight = 50

export const calendarDefaultOptions: Record<$CalendarZoomLevel, CalendarOptions> = {
  week: {
    fontSize: 'Tiny',
    warningStatus: 'On',
    location: 'Off',
    clientName: 'Short',
    member: 'Off',
    treatments: 'Initials',
    repeatClient: 'Off',
    time: 'Off',
    duration: 'Off',
    colors: 'Treatment',
  },
  fiveDay: {
    fontSize: 'Small',
    warningStatus: 'On',
    location: 'Off',
    clientName: 'Short',
    member: 'Off',
    treatments: 'Initials',
    repeatClient: 'Off',
    time: 'Off',
    duration: 'Off',
    colors: 'Treatment',
  },
  threeDay: {
    fontSize: 'Small',
    warningStatus: 'On',
    location: 'Off',
    clientName: 'Short',
    member: 'Off',
    treatments: 'Full',
    repeatClient: 'Off',
    time: 'Off',
    duration: 'Off',
    colors: 'Treatment',
  },
  day: {
    fontSize: 'Medium',
    warningStatus: 'On',
    location: 'Off',
    clientName: 'Short',
    member: 'Off',
    treatments: 'Full',
    repeatClient: 'Off',
    time: 'Off',
    duration: 'On',
    colors: 'Treatment',
  },
}

const isCalendarZoomLevel = (value: unknown): value is $CalendarZoomLevel => {
  return value === 'week' || value === 'fiveDay' || value === 'threeDay' || value === 'day'
}

const isCalendarView = (value: unknown): value is $CalendarView => {
  return value === 'team' || isCalendarZoomLevel(value)
}

class UIStore {
  store: $RootStore
  actionMenuVisible = false
  actionMenuData: $ActionMenuData = {}
  message: $UIMessage = {}
  openModalDrawers: Set<string> = new Set()
  activeRequests: (Operation & { id: string, operationName: keyof Query | keyof Mutation })[] = []
  todoNumbers = defaultNumbers
  migrationTool: string | undefined
  homeStats: StatsKeysWithSource[] = []
  /** Active calendar layout: team (member columns) or a day-span zoom. */
  calendarView: $CalendarView = 'week'
  /** Last non-team zoom, restored when leaving team view. */
  calendarLastZoomLevel: $CalendarZoomLevel = 'week'
  calendarOptions: Record<$CalendarZoomLevel, CalendarOptions> = { ...calendarDefaultOptions }
  calendarHourHeight = defaultCalendarHourHeight
  /** Selected member(s) for non-team salon calendar. Array so multi-select can land later. */
  calendarSelectedTeamMemberIds: string[] = []
  seenPrompts: Array<string> = []
  isSmallScreen = false
  // The static location used on the last booking, so new bookings can default to it.
  lastBookingLocationId = ''

  constructor(store: $RootStore) {
    this.store = store
    makeAutoObservable(this)
  }

  get calendarZoomLevel(): $CalendarZoomLevel {
    return this.calendarView === 'team' ? this.calendarLastZoomLevel : this.calendarView
  }

  setCalendarView = (view: $CalendarView) => {
    if (view !== 'team') {
      this.calendarLastZoomLevel = view
    }
    this.calendarView = view
    this.syncInStorage()
  }

  shouldRenderUI = (pathname: string): boolean => {
    return this.store.ready && (this.store.isPublicRoute(pathname) || this.store.authStore.isLoggedIn)
  }

  showActionMenu = (actionMenuData: $ActionMenuData) => {
    this.actionMenuData = actionMenuData
    this.actionMenuVisible = true
  }

  showBrowserUnsupportedMessage = () => {
    const { s } = this.store.i18n

    this.showMessage({
      title: s('Browser not supported'),
      body: s('Sorry, your browser is currently not supported. We recommend upgrading your browser to avoid encountering issues and bugs.'),
      buttons: [
        { title: s('Continue without updating'), type: 'secondary' },
      ],
    })
  }

  checkBrowserSupport = (pathname: string) => {
    if (pathname.startsWith('/auth/') || pathname.startsWith('/setup/')) return

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)

    if (isSafari) {
      const minSafariVersion = 15
      const version = navigator.userAgent.match(/Version\/(\d+)/)?.[1]
      if (version && parseInt(version) < minSafariVersion) {
        this.showBrowserUnsupportedMessage()
      }

      return
    }

    const isChromium = /chrome/i.test(navigator.userAgent)
    if (isChromium) {
      const minChromeVersion = 120
      const version = navigator.userAgent.match(/Chrome\/(\d+)/)?.[1]
      if (version && parseInt(version) < minChromeVersion) {
        this.showBrowserUnsupportedMessage()
      }

      return
    }

    this.showBrowserUnsupportedMessage()
  }

  closeActionMenu = () => {
    this.actionMenuVisible = false
  }

  updateIsSmallScreen = (update: boolean = true) => {
    this.isSmallScreen = update
    this.syncInStorage()
  }

  setLastBookingLocationId = (locationId: string) => {
    this.lastBookingLocationId = locationId
    this.syncInStorage()
  }

  showToast = (message: string, type?: DropdownAlertType, time = 4000) => {
    this.store.dropdownMessageHandler.showDropdownMessage({
      message,
      type: (type || 'error') as DropdownAlertType,
      closeInterval: time,
    })
  }

  showNotImplemented = () => {
    this.showToast(this.store.i18n.s("This hasn't been implemented yet. Check back soon!"), 'info')
  }

  showMessage = (message: $UIMessage) => {
    if (!this.message.important || message.important) {
      this.message = message
    }

    this.message.visible = true
  }

  hideMessage = () => {
    this.message.visible = false
    this.message.important = false
  }

  openPhone = (mobile: string) => {
    alert('TODO: ' + mobile)
  }

  copyToClipboard = (text: string, message?: string) => {
    navigator.clipboard.writeText(text)
    if (message) {
      this.showToast(this.store.i18n.s(message), 'info')
    }
  }

  triggerHaptic = () => {
    // NATIVE:
  }

  dismissKeyboard = () => {
    // NATIVE: Keyboard.dismiss()
  }

  openMail = (email: string, subject: string, body: string) => {
    this.openLink(`mailto:${email}?subject=${subject}&body=${body}`)
  }

  openLink = (url: string, target?: '_blank' | '_self') => {
    window.open(url, target || '_blank')
  }

  getColor = (color: string | undefined) => {
    const couponColor = color ? `${color}Color` as keyof typeof iconColors : undefined
    const colorKey = couponColor ? iconColors[couponColor] : undefined
    return colorKey ?? iconColors.greenColor
  }

  showUnsavedChangesMessage = (
    blockerOrOnDiscard: Blocker | (() => void),
    customBody?: string,
  ) => {
    const isBlocker = 'reset' in blockerOrOnDiscard

    this.showMessage({
      title: 'Unsaved changes',
      body: customBody || 'Are you sure you want to leave this page?',
      buttons: [
        {
          title: 'Keep changes',
          type: 'primary',
          onPress: isBlocker
            ? () => (blockerOrOnDiscard as Blocker).reset?.()
            : () => { },
        },
        {
          title: 'Discard changes',
          type: 'secondary',
          onPress: isBlocker
            ? () => (blockerOrOnDiscard as Blocker).proceed?.()
            : () => (blockerOrOnDiscard as () => void)(),
        },
      ],
    })
  }

  showNetworkError = (error?: { message?: string; queryName?: string }) => {
    const { s } = this.store.i18n

    // Clear loading spinner
    this.activeRequests = []

    const communicationHasLoaded = this.store.ready

    let body = s('Please check your internet connection and try again.')
    if (error?.message) {
      body = error.message
    }

    this.showMessage({
      icon: 'noConnection',
      title: s('Network error'),
      body,
      buttons: [
        {
          title: s('Try again'),
          type: 'primary',
          onPress: () => {
            // Refresh the page if the communication has not loaded
            if (!communicationHasLoaded) {
              window.location.reload()
            }

            // Otherwise we can just close the modal
            this.hideMessage()
          },
        },
      ],
    })
  }

  goToSubscriptions = () => {
    this.openLink('/subscriptions/manage')
  }

  showInformationModal = (message: $UIMessage) => {
    this.showMessage(message)
  }

  openModalDrawer = (pathname: string) => {
    this.openModalDrawers.add(pathname)
  }

  closeModalDrawer = (pathname: string) => {
    this.openModalDrawers.delete(pathname)
  }

  syncInStorage = (): void => {
    this.store.storeDataSync.saveStoreToStorage('UI')
  }

  // Add value to a variable
  @action.bound inflate(data: any) {
    const {
      calendarView,
      calendarLastZoomLevel,
      calendarSelectedTeamMemberIds,
      calendarOptions,
      // Legacy keys — migrate then drop
      calendarZoomLevel,
      calendarTeamView,
      calendarSelectedTeamMemberId,
      ...rest
    } = data ?? {}

    Object.keys(rest).forEach(key => {
      this[key as keyof this] = rest[key]
    })

    if (isCalendarView(calendarView)) {
      this.calendarView = calendarView
    } else if (calendarTeamView) {
      this.calendarView = 'team'
    } else if (isCalendarZoomLevel(calendarZoomLevel)) {
      this.calendarView = calendarZoomLevel
    }

    if (isCalendarZoomLevel(calendarLastZoomLevel)) {
      this.calendarLastZoomLevel = calendarLastZoomLevel
    } else if (isCalendarZoomLevel(calendarZoomLevel)) {
      this.calendarLastZoomLevel = calendarZoomLevel
    } else if (this.calendarView !== 'team') {
      this.calendarLastZoomLevel = this.calendarView
    }

    // stored settings layer on top of the defaults, otherwise
    //  we get undefined for options that didn't exist yet
    if (calendarOptions) {
      this.calendarOptions = calendarZoomLevels.reduce((merged, level) => ({
        ...merged,
        [level]: { ...calendarDefaultOptions[level], ...calendarOptions[level] },
      }), {} as Record<$CalendarZoomLevel, CalendarOptions>)
    }

    if (Array.isArray(calendarSelectedTeamMemberIds)) {
      this.calendarSelectedTeamMemberIds = calendarSelectedTeamMemberIds.filter(
        (id): id is string => typeof id === 'string',
      )
    } else if (typeof calendarSelectedTeamMemberId === 'string') {
      this.calendarSelectedTeamMemberIds = [calendarSelectedTeamMemberId]
    }
  }

  @action.bound clearAccountData() {
    this.todoNumbers = defaultNumbers
    this.actionMenuVisible = false
    this.actionMenuData = {}
    this.migrationTool = undefined
    this.homeStats = []
    this.calendarSelectedTeamMemberIds = []
    this.seenPrompts = []
    this.lastBookingLocationId = ''
    this.syncInStorage()
  }

  @action.bound clearData() {
    this.calendarView = 'week'
    this.calendarLastZoomLevel = 'week'
    this.calendarOptions = { ...calendarDefaultOptions }
    this.calendarHourHeight = defaultCalendarHourHeight
    this.isSmallScreen = false
    this.clearAccountData()
  }

  serialize() {
    return {
      todoNumbers: this.todoNumbers,
      homeStats: this.homeStats,
      migrationTool: this.migrationTool,
      calendarView: this.calendarView,
      calendarLastZoomLevel: this.calendarLastZoomLevel,
      calendarOptions: this.calendarOptions,
      calendarHourHeight: this.calendarHourHeight,
      calendarSelectedTeamMemberIds: this.calendarSelectedTeamMemberIds,
      seenPrompts: this.seenPrompts,
      isSmallScreen: this.isSmallScreen,
      lastBookingLocationId: this.lastBookingLocationId,
    }
  }
}

export default UIStore
